import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { Timer } from 'three/addons/misc/Timer.js'
import GUI from 'lil-gui'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js'
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js'
import { setupLights } from './lights.js'

/**
 * ========================================
 * STORY & DIALOG SYSTEM
 * ========================================
 */

// Story scenes dengan posisi kamera
const storyScenes = [
    {
        text: "This is the best place ever. A place where the warmth of family gathers",
        camera: { x: 0.960, y: 3.232, z: 4.316 }, // posisi awal
        target: { x: 0, y: 0, z: 0 }
    },
    {
        text: "This bonfire has been burning since morning, providing warmth, light, and a cup of brewed coffee.",
        camera: { x: -3.441, y: 0.815, z: -0.984 }, // dekat api
        target: { x: -3.5, y: 0.5, z: -2.5 }
    },
    {
        text: "This bamboo bed is a place to rest while enjoying the peaceful atmosphere of the kitchen.",
        camera: { x: 3.821, y: 1.521, z: 3.776 }, // area dipan
        target: { x: 1.0, y: 0.5, z: 0 }
    },
    {
        text: "A cup of warm coffee accompanies the quiet afternoon, while waiting to cook something.",
        camera: { x: -1.0, y: 1.2, z: 2.0 }, // area kopi (sesuaikan posisinya)
        target: { x: -0.5, y: 0.8, z: 1.0 }
    }
]


let currentSceneIndex = 0
let isTyping = false
let typingInterval = null

// Elements
const introDialog = document.getElementById('intro-dialog')
const btnStart = document.getElementById('btn-start')
const storyDialog = document.getElementById('story-dialog')
const storyTextElement = document.getElementById('story-text')
const btnNext = document.getElementById('btn-next')
const btnSkip = document.getElementById('btn-skip')
const btnExitStory = document.getElementById('btn-exit-story')

// Menu elements
const menuButton = document.getElementById('menu-button')
const infoPanel = document.getElementById('info-panel')
const btnCloseInfo = document.getElementById('btn-close-info')

// Menu handlers
menuButton.addEventListener('click', () => {
    infoPanel.classList.add('active')
})

btnCloseInfo.addEventListener('click', () => {
    infoPanel.classList.remove('active')
})

// Close info panel when clicking on overlay background
infoPanel.addEventListener('click', (e) => {
    // Only close if clicked directly on the overlay (not on content)
    if (e.target === infoPanel) {
        infoPanel.classList.remove('active')
    }
})

/**
 * ========================================
 * STORY & DIALOG SYSTEM FUNCTIONS
 * ========================================
 */

// Typing effect function
function typeText(text, element, callback) {
    element.textContent = ''
    let index = 0
    isTyping = true
    btnNext.disabled = true
    btnSkip.classList.remove('hidden')
    
    // Add cursor
    const cursor = document.createElement('span')
    cursor.className = 'typing-cursor'
    element.appendChild(cursor)
    
    typingInterval = setInterval(() => {
        if (index < text.length) {
            element.textContent = text.substring(0, index + 1)
            element.appendChild(cursor)
            index++
        } else {
            clearInterval(typingInterval)
            cursor.remove()
            isTyping = false
            btnNext.disabled = false
            btnSkip.classList.add('hidden')
            if (callback) callback()
        }
    }, 50) // Kecepatan ketik (ms per karakter)
}

// Skip typing animation
function skipTyping() {
    if (isTyping && typingInterval) {
        clearInterval(typingInterval)
        const scene = storyScenes[currentSceneIndex]
        storyTextElement.textContent = scene.text
        const cursor = storyTextElement.querySelector('.typing-cursor')
        if (cursor) cursor.remove()
        isTyping = false
        btnNext.disabled = false
        btnSkip.classList.add('hidden')
    }
}

// Exit story dialog
function exitStoryDialog() {
    if (typingInterval) {
        clearInterval(typingInterval)
    }
    storyDialog.classList.remove('active')
    isTyping = false
    btnNext.disabled = false
    btnSkip.classList.add('hidden')
    // Re-enable orbit controls fully
    controls.enabled = true
}

// Animate camera to target position
function animateCamera(targetPos, targetLookAt, duration = 2000) {
    const startPos = {
        x: camera.position.x,
        y: camera.position.y,
        z: camera.position.z
    }
    
    const startTarget = {
        x: controls.target.x,
        y: controls.target.y,
        z: controls.target.z
    }
    
    const startTime = Date.now()
    
    function updateCamera() {
        const elapsed = Date.now() - startTime
        const progress = Math.min(elapsed / duration, 1)
        
        // Easing function (ease-in-out)
        const eased = progress < 0.5
            ? 2 * progress * progress
            : 1 - Math.pow(-2 * progress + 2, 2) / 2
        
        // Interpolate camera position
        camera.position.x = startPos.x + (targetPos.x - startPos.x) * eased
        camera.position.y = startPos.y + (targetPos.y - startPos.y) * eased
        camera.position.z = startPos.z + (targetPos.z - startPos.z) * eased
        
        // Interpolate camera target
        controls.target.x = startTarget.x + (targetLookAt.x - startTarget.x) * eased
        controls.target.y = startTarget.y + (targetLookAt.y - startTarget.y) * eased
        controls.target.z = startTarget.z + (targetLookAt.z - startTarget.z) * eased
        
        controls.update()
        
        if (progress < 1) {
            requestAnimationFrame(updateCamera)
        }
    }
    
    updateCamera()
}

// Show story scene
function showStoryScene(index) {
    if (index >= storyScenes.length) {
        // Story selesai, sembunyikan dialog
        storyDialog.classList.remove('active')
        // Re-enable orbit controls fully
        controls.enabled = true
        return
    }
    
    const scene = storyScenes[index]
    
    // Animate camera
    animateCamera(scene.camera, scene.target)
    
    // Show dialog with typing effect
    storyDialog.classList.add('active')
    typeText(scene.text, storyTextElement)
}

// Handle intro start button
btnStart.addEventListener('click', () => {
    introDialog.classList.remove('active')
    
    setTimeout(() => {
        introDialog.style.display = 'none'
        
        // Show menu button after intro
        setTimeout(() => {
            menuButton.classList.add('visible')
        }, 300)
        
        // Start story after intro fades
        setTimeout(() => {
            showStoryScene(0)
        }, 500)
    }, 500)
})

// Handle next button
btnNext.addEventListener('click', () => {
    if (isTyping) {
        skipTyping()
    } else {
        // Go to next scene
        currentSceneIndex++
        showStoryScene(currentSceneIndex)
    }
})

// Handle skip button
btnSkip.addEventListener('click', () => {
    skipTyping()
})

// Handle exit button
btnExitStory.addEventListener('click', () => {
    exitStoryDialog()
})

/**
 * Base
 */
// Debug
const gui = new GUI()
if (window.location.hash !== '#dev') {
    // hide GUI for non-dev usage
    if (typeof gui.hide === 'function') {
        gui.hide()
    } else {
        gui.domElement.style.display = 'none'
    }
}
// Canvas
const canvas = document.querySelector('canvas.webgl')

// Scene
const scene = new THREE.Scene()

/**
 * Audio
 */
// Audio Setup
const listener = new THREE.AudioListener()
const audioLoader = new THREE.AudioLoader()
const backgroundSound = new THREE.Audio(listener)

// Load and play background audio
audioLoader.load(
    // '/audio/lofi-sample-if-i-cant-have-you-330746.mp3', // adjust path to your audio file
    '/audio/nature-birds-ambiance-morning-kisses-214774.mp3', // adjust path to your audio file
    (buffer) => {
        backgroundSound.setBuffer(buffer)
        backgroundSound.setLoop(true)
        backgroundSound.setVolume(0.5)
        backgroundSound.play()
        
        // Create play button (required by browser policies)
        const audioBtn = document.createElement('button')
        audioBtn.innerHTML = '🔊 Pause Audio'
        audioBtn.style.position = 'fixed'
        audioBtn.style.bottom = '20px'
        audioBtn.style.right = '20px'
        audioBtn.style.padding = '8px 12px'
        audioBtn.style.border = 'none'
        audioBtn.style.borderRadius = '4px'
        audioBtn.style.background = 'rgba(0,0,0,0.5)'
        audioBtn.style.color = 'white'
        audioBtn.style.cursor = 'pointer'
        audioBtn.style.zIndex = '1000'
        
        audioBtn.onclick = () => {
            if (backgroundSound.isPlaying) {
                backgroundSound.pause()
                audioBtn.innerHTML = '🔈 Play Audio'
            } else {
                backgroundSound.play()
                audioBtn.innerHTML = '🔊 Pause Audio'
            }
        }
        
        document.body.appendChild(audioBtn)
    },
    (progress) => {
        console.log('Audio loading:', (progress.loaded / progress.total * 100) + '%')
    },
    (error) => {
        console.error('Error loading audio:', error)
    }
)


/**
 * Model
 */
let mixer = null
const gltfLoader = new GLTFLoader()

gltfLoader.load(
    '/fire/Fire.glb',
    (gltf) => {
        scene.add(gltf.scene)
        gltf.scene.position.set(-3.5, 0.2, -3)
        gltf.scene.scale.set(1.3, 1, 1.3)
        // Traverse and setup materials
        gltf.scene.traverse((child) => {
            if (child.isMesh) {
                // Make the material emissive for bloom effect
                child.material.emissive = new THREE.Color(0xf97810) // Orange fire color
                child.material.emissiveIntensity = 1.1

                if (child.material.name === 'FireOut') {
                    child.material.transparent = true
                    child.material.opacity = 0.48
                    // Add controls to GUI
                    const materialFolder = gui.addFolder('Fire Material')
                    materialFolder.add(child.material, 'opacity', 0, 1, 0.01)
                    materialFolder.add(child.material, 'emissiveIntensity', 0, 5, 0.1)
                    materialFolder.addColor(child.material, 'emissive')
                }
            }
        })
        
        // Create animation mixer
        mixer = new THREE.AnimationMixer(gltf.scene)
        
        // Play all animations
        gltf.animations.forEach((animation) => {
            mixer.clipAction(animation).play()
        })
    },
    (progress) => {
        console.log('Loading progress:', (progress.loaded / progress.total * 100) + '%')
    },
    (error) => {
        console.error('Error loading model:', error)
    }
)

const textureLoader = new THREE.TextureLoader()
// Load building GLB
gltfLoader.load(
    '/building/Pawon01.glb', // sesuaikan path ke file .glb Anda
    (gltf) => {
        const building = gltf.scene
        // sesuaikan skala / posisi jika perlu
        building.scale.set(1, 1, 1)
        building.position.set(0, 0, 0)
        building.rotation.set(0, 0, 0)

        // enable shadows & material fixes
        building.traverse((child) => {
            if (child.isMesh) {
                child.castShadow = true
                child.receiveShadow = true

                // non-emissive agar bloom tidak menyala secara tidak sengaja
                if (child.material.emissive) {
                    child.material.emissive.set(0x000000)
                    child.material.emissiveIntensity = 0
                }

                /////
                // Check for fog mesh
                if (child.name.includes('Fog')) {
                    // Clone material to avoid affecting other meshes
                    child.material = child.material.clone()
                    child.castShadow = false
                    child.receiveShadow = false  

                    // Load alpha texture
                    const alphaTexture = textureLoader.load('/textures/fog1.png') // adjust path to your texture
    
                    // Setup fog material properties
                    child.material.transparent = true
                    child.material.opacity = 0.07
                    child.material.emissive = new THREE.Color(0x808080)
                    child.material.emissiveIntensity = 5.0
                    
                    // Add GUI controls for Fog mesh
                    const fogFolder = gui.addFolder('Fog Material')
                    fogFolder.add(child.material, 'opacity', 0, 1, 0.01).name('Transparency')
                    fogFolder.add(child.material, 'emissiveIntensity', 0, 5, 0.1).name('Glow Intensity')

                    // Single color picker for emissive color
                    const emissiveColor = { color: '#808080' }
                    fogFolder.addColor(emissiveColor, 'color')
                        .name('Glow Color')
                        .onChange((value) => {
                            child.material.emissive.set(value)
                        })
                    fogFolder.open()
                }
                if (child.name.includes('Kusen')) {
                    child.material = child.material.clone()
                    child.castShadow = false
                    child.receiveShadow = true  
                }
            }
        })

        scene.add(building)

        // jika GLTF memiliki animasi
        if (gltf.animations && gltf.animations.length) {
            const buildingMixer = new THREE.AnimationMixer(building)
            gltf.animations.forEach((clip) => buildingMixer.clipAction(clip).play())
            // simpan mixer jika ingin update di tick (opsional)
        }

        console.log('Building loaded')
    },
    (progress) => {
        console.log('Building loading:', Math.round(progress.loaded / progress.total * 100) + '%')
    },
    (err) => {
        console.error('Error loading building:', err)
    }
)

// Setup lights via module
const lights = setupLights(scene, gui)

/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}

window.addEventListener('resize', () =>
{
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

    // Update effect composer
    effectComposer.setSize(sizes.width, sizes.height)
    effectComposer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
})

/**
 * Camera
 */
// Base camera
const camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 100)
camera.position.x = 0.960
camera.position.y = 3.232
camera.position.z = 4.316
scene.add(camera)


// Controls
const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true
// Batasi rotasi vertikal (polar): mis. 20° - 80°
controls.minPolarAngle = THREE.MathUtils.degToRad(20)  // tidak boleh melihat terlalu ke atas
controls.maxPolarAngle = THREE.MathUtils.degToRad(80)  // tidak boleh melihat terlalu ke bawah

// Batasi rotasi horizontal (azimuth): mis. -45°..45°
controls.minAzimuthAngle = THREE.MathUtils.degToRad(-45)
controls.maxAzimuthAngle = THREE.MathUtils.degToRad(45)
// Batasi turun ke bawah



/**
 * Renderer
 */
const renderer = new THREE.WebGLRenderer({
    canvas: canvas
})
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))

// enable shadows
renderer.shadowMap.enabled = true
renderer.shadowMap.type = THREE.PCFSoftShadowMap

/**
 * Post Processing
 */
// Render Target
const renderTarget = new THREE.WebGLRenderTarget(
    800,
    600,
    {
        samples: renderer.getPixelRatio() === 1 ? 2 : 0
    }
)


// Effect Composer
const effectComposer = new EffectComposer(renderer, renderTarget)
effectComposer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
effectComposer.setSize(sizes.width, sizes.height)

// Render Pass
const renderPass = new RenderPass(scene, camera)
effectComposer.addPass(renderPass)

renderer.shadowMap.enabled = true
renderer.shadowMap.type = THREE.PCFSoftShadowMap
renderer.shadowMap.autoUpdate = true
renderer.toneMapping = THREE.ACESFilmicToneMapping
renderer.toneMappingExposure = 1

// Bloom Pass
const bloomPass = new UnrealBloomPass()
bloomPass.strength = 2.3
bloomPass.radius = 0.84
bloomPass.threshold = 0.3
effectComposer.addPass(bloomPass)

// Add Bloom controls to GUI
const bloomFolder = gui.addFolder('Bloom Effects')
bloomFolder.add(bloomPass, 'strength').min(0).max(3).step(0.01)
bloomFolder.add(bloomPass, 'radius').min(0).max(1).step(0.01)
bloomFolder.add(bloomPass, 'threshold').min(0).max(1).step(0.01)

/**
 * Animate
 */
const timer = new Timer()

const tick = () =>
{
    
    // console.log(camera.position)
    // Timer
    timer.update()
    const elapsedTime = timer.getElapsed()

    // Update animations
    if(mixer) {
        mixer.update(timer.getDelta())
    }

    // Update controls
    controls.update()

     // Update helpers position / matrices
    // lights.dirLightHelper.update()
    // lights.dirLightCameraHelper.update()
    lights.lightSphere.position.copy(lights.directionalLight.position)
    lights.lightSphere2.position.copy(lights.directionalLight2.position)
    // sync ambient helper color
    lights.ambientHelper.material.color.copy(lights.ambientLight.color).multiplyScalar(lights.ambientLight.intensity)

    // Render
    // renderer.render(scene, camera)
    
    // Render with effect composer instead of regular renderer
    effectComposer.render()

    // Call tick again on the next frame
    window.requestAnimationFrame(tick)
}

tick()